import { ChevronDown, ChevronRight, Info } from 'lucide-preact'
import { RadioGroup } from './RadioGroup'
import type { RadioGroupOption } from './RadioGroup'
import type { RoleType } from '@/types/userRoles'
import type { ClientWorkspaceViewDto } from '@/types/company'

// Available role types - this could come from a constants file or API
const AVAILABLE_ROLES: RoleType[] = ['Viewer', 'Editor', 'Admin']

interface RoleSelectorProps {
  companyRoles: string[]
  workspaceRoles: Record<string, string[]>
  selectedWorkspaces: string[]
  companyWorkspaces: ClientWorkspaceViewDto[]
  onCompanyRoleChange: (role: string, checked: boolean) => void
  onWorkspaceRoleChange: (workspaceId: string, role: string, checked: boolean) => void
  collapsedWorkspaces?: Set<string>
  onToggleWorkspaceCollapse?: (workspaceId: string) => void
  showEffectiveRoles?: boolean
}

/** Build RadioGroup options for company roles */
function getCompanyRoleOptions(): RadioGroupOption[] {
  return AVAILABLE_ROLES.map(role => ({ value: role, label: role }))
}

/** Build RadioGroup options for a workspace, disabling roles already granted at company level */
function getWorkspaceRoleOptions(companyRoles: string[]): RadioGroupOption[] {
  return AVAILABLE_ROLES.map(role => ({
    value: role,
    label: role,
    disabled: companyRoles.includes(role),
    description: companyRoles.includes(role) ? 'Granted by company role' : undefined
  }))
}

export function RoleSelector({
  companyRoles,
  workspaceRoles,
  selectedWorkspaces,
  companyWorkspaces,
  onCompanyRoleChange,
  onWorkspaceRoleChange,
  collapsedWorkspaces = new Set(),
  onToggleWorkspaceCollapse,
  showEffectiveRoles = true
}: RoleSelectorProps) {

  // Get effective roles for preview (company roles override workspace roles)
  const getEffectiveRoles = (): { company: string[]; workspaces: Record<string, string[]> } => {
    const effectiveWorkspaceRoles: Record<string, string[]> = {}

    selectedWorkspaces.forEach(workspaceId => {
      const wsRoles = workspaceRoles[workspaceId] || []
      // Remove any workspace roles that are already granted at company level
      effectiveWorkspaceRoles[workspaceId] = wsRoles.filter(role => !companyRoles.includes(role))
    })

    return {
      company: companyRoles,
      workspaces: effectiveWorkspaceRoles
    }
  }

  const effectiveRoles = getEffectiveRoles()
  const companyRoleOptions = getCompanyRoleOptions()
  const workspaceRoleOptions = getWorkspaceRoleOptions(companyRoles)

  return (
    <div className="space-y-6">
      {/* Company Roles Section */}
      <div>
        <RadioGroup
          label="Company Roles"
          name="company-role"
          options={companyRoleOptions}
          value={companyRoles[0] || null}
          onChange={(val) => onCompanyRoleChange(val as string, val !== null)}
          allowDeselect
          layout="grid"
          helperText="Company roles apply to all workspaces and override any workspace-specific roles."
        />
      </div>

      {/* Workspace Roles Section */}
      <div>
        <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-3">
          Workspace Roles
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
          Workspace roles apply only to specific workspaces. These are overridden by company roles.
        </p>

        {companyWorkspaces.length === 0 ? (
          <div className="text-center py-6 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <p className="text-slate-500 dark:text-slate-400">No workspaces available</p>
          </div>
        ) : (
          <div className="space-y-4">
            {companyWorkspaces.map(workspace => {
              const isCollapsed = collapsedWorkspaces.has(workspace.Id)
              const wsRoles = workspaceRoles[workspace.Id] || []
              const hasRoles = wsRoles.length > 0

              return (
                <div key={workspace.Id} className="border border-slate-200 dark:border-slate-600 rounded-lg">
                  {/* Workspace header */}
                  <div
                    className="p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center justify-between"
                    onClick={() => onToggleWorkspaceCollapse?.(workspace.Id)}
                  >
                    <div className="flex items-center space-x-3">
                      {onToggleWorkspaceCollapse && (
                        isCollapsed ?
                          <ChevronRight className="w-4 h-4 text-slate-400" /> :
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                      )}
                      <div>
                        <h4 className="font-medium text-slate-900 dark:text-white">
                          {workspace.Name}
                        </h4>
                        {hasRoles && (
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {wsRoles.length} role{wsRoles.length !== 1 ? 's' : ''} assigned
                          </p>
                        )}
                      </div>
                    </div>

                    {hasRoles && (
                      <div className="flex flex-wrap gap-1">
                        {wsRoles.map(role => (
                          <span
                            key={role}
                            className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs rounded"
                          >
                            {role}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Workspace roles (collapsible) */}
                  {!isCollapsed && (
                    <div className="px-4 pb-4 border-t border-slate-200 dark:border-slate-600">
                      <div className="mt-3">
                        <RadioGroup
                          name={`workspace-role-${workspace.Id}`}
                          options={workspaceRoleOptions}
                          value={wsRoles[0] || null}
                          onChange={(val) => onWorkspaceRoleChange(workspace.Id, val as string, val !== null)}
                          allowDeselect
                          layout="grid"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Effective Roles Preview */}
      {showEffectiveRoles && (companyRoles.length > 0 || selectedWorkspaces.length > 0) && (
        <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
          <div className="flex items-start space-x-2 mb-3">
            <Info className="w-4 h-4 text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-slate-900 dark:text-white text-sm">
                Effective Permissions
              </h4>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                This shows the actual permissions the user will have after company roles override workspace roles.
              </p>
            </div>
          </div>

          {/* Company roles preview */}
          {effectiveRoles.company.length > 0 && (
            <div className="mb-3">
              <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Company-wide permissions:
              </div>
              <div className="flex flex-wrap gap-1">
                {effectiveRoles.company.map(role => (
                  <span
                    key={role}
                    className="px-2 py-1 bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 text-xs rounded font-medium"
                  >
                    {role}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Workspace roles preview */}
          {Object.keys(effectiveRoles.workspaces).length > 0 && (
            <div>
              <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Workspace-specific permissions:
              </div>
              <div className="space-y-2">
                {Object.entries(effectiveRoles.workspaces).map(([workspaceId, roles]) => {
                  if (roles.length === 0) return null
                  const workspace = companyWorkspaces.find(d => d.Id === workspaceId)
                  return (
                    <div key={workspaceId} className="text-sm">
                      <span className="text-slate-600 dark:text-slate-400 font-medium">
                        {workspace?.Name || 'Unknown Workspace'}:
                      </span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {roles.map(role => (
                          <span
                            key={role}
                            className="px-2 py-1 bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-300 text-xs rounded"
                          >
                            {role}
                          </span>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
